import { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useNotificationStore } from '@store'
import NotificationDropdown from './NotificationDropdown'
import './NotificationBell.scss'

/**
 * NotificationBell Component
 *
 * Bell icon button that shows unread count badge and toggles notification dropdown.
 * Automatically polls for new notifications when mounted.
 */
const NotificationBell = ({
	pollingInterval = 30000,
	showBadge = true,
	size = 'medium'
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const containerRef = useRef(null)
	const buttonRef = useRef(null)

	const {
		unreadCount,
		startPolling,
		stopPolling,
		fetchUnreadCount
	} = useNotificationStore()

	// Start polling on mount
	useEffect(() => {
		// Fetch initial count
		fetchUnreadCount()

		// Start polling
		const cleanup = startPolling(pollingInterval)

		return () => {
			cleanup()
			stopPolling()
		}
	}, [pollingInterval, startPolling, stopPolling, fetchUnreadCount])

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			document.addEventListener('touchstart', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('touchstart', handleClickOutside)
		}
	}, [isOpen])

	// Toggle dropdown
	const toggleDropdown = useCallback(() => {
		setIsOpen(prev => !prev)
	}, [])

	// Close dropdown
	const closeDropdown = useCallback(() => {
		setIsOpen(false)
		buttonRef.current?.focus()
	}, [])

	// Handle keyboard navigation
	const handleKeyDown = useCallback((e) => {
		if (e.key === 'Escape' && isOpen) {
			closeDropdown()
		}
	}, [isOpen, closeDropdown])

	// Format badge count
	const getBadgeCount = () => {
		if (unreadCount <= 0) return null
		if (unreadCount > 99) return '99+'
		return unreadCount.toString()
	}

	const badgeCount = getBadgeCount()

	return (
		<div
			ref={containerRef}
			className={`notification-bell notification-bell--${size}`}
			onKeyDown={handleKeyDown}
		>
			<button
				ref={buttonRef}
				className={`notification-bell__button ${isOpen ? 'notification-bell__button--active' : ''}`}
				onClick={toggleDropdown}
				aria-label={`Notifications${badgeCount ? `, ${badgeCount} unread` : ''}`}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				type="button"
			>
				{/* Bell Icon */}
				<svg
					className="notification-bell__icon"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
					<path d="M13.73 21a2 2 0 0 1-3.46 0" />
				</svg>

				{/* Badge */}
				{showBadge && badgeCount && (
					<span className="notification-bell__badge" aria-hidden="true">
						{badgeCount}
					</span>
				)}

				{/* Pulse animation for new notifications */}
				{unreadCount > 0 && (
					<span className="notification-bell__pulse" aria-hidden="true" />
				)}
			</button>

			{/* Dropdown */}
			<NotificationDropdown
				isOpen={isOpen}
				onClose={closeDropdown}
			/>
		</div>
	)
}

NotificationBell.propTypes = {
	pollingInterval: PropTypes.number,
	showBadge: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'medium', 'large'])
}

export default NotificationBell
